import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { ModalHeader } from '~/components/Modal/components';
import Modal from '~/components/Modal/Modal';
import { useCartVibration } from '~/hooks/useCartVibration';
import { useProductChoices } from '~/hooks/useProductChoices';
import { useProductLimit } from '~/hooks/useProductLimit';
import { useStoreInstance } from '~/hooks/useStoreInstance';
import { cartStore, catalogStore, orderStore } from '~/stores';
import { ProductState } from '~/stores/CategoriesStore';
import { isProductBundle } from '~/types/Product';

import { useModal } from '../../hooks/useModal';

import { Button } from '../isolated/Button';
import { Counter } from '../isolated/Counter';
import { SIZE } from '../isolated/IconButton';
import ProductCardV3 from '../ProductCardV3';
import ProductPropertyFilter from '../ProductPropertyFilterV3/ProductPropertyFilter';
import SkeletonListRows from '../Skeleton/SkeletonList';

import styles from './ModalSelectProductSKU.module.scss';

const SelectProductSkeleton = () => {
  return (
    <div className={styles.form}>
      <p className={styles.title}>
        <Skeleton />
      </p>
      <div className={styles.formInfo}>
        <Skeleton
          className={classes([styles.product, styles.productSkeleton])}
        />
        <div className={styles.choices}>
          <SkeletonListRows rows={2}>
            <div className={styles.skeletonChoice}>
              <p className={styles.skeletonListTitle}>
                <Skeleton />
              </p>
              <div className={styles.skeletonList}>
                <SkeletonListRows rows={4}>
                  <Skeleton className={styles.skeletonListItem} />
                </SkeletonListRows>
              </div>
            </div>
          </SkeletonListRows>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.control}>
          <Skeleton className={styles.footerButton} />
        </div>
      </div>
    </div>
  );
};

export default observer(() => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const slug = catalogStore.selectedCatalogProduct;
  const product = useStoreInstance(ProductState, slug || '');

  const getProduct = async () => {
    if (slug) {
      await product.request.run({
        productId: slug || '',
        warehouse: orderStore.etaWarehouseCode,
      });
    }
  };

  useEffect(() => {
    slug && getProduct();
  }, [orderStore, slug]);

  if (!slug) {
    closeModal();
  }

  const handleChangeOffer = (sku: string) => () => {
    product.selectOffer(sku);
  };

  const currentProductFilters = useProductChoices({ product });
  const filterValues = Object.values(currentProductFilters);
  const modalTitle =
    filterValues.length === 1
      ? `Select ${filterValues && filterValues[0] ? filterValues[0].name : ''}`
      : 'Select parameters';

  const backHandler = () => {
    closeModal();
    catalogStore.setSelectedCatalogProduct(null);
  };

  const isBundle = product.data ? isProductBundle(product.data) : false;

  const currentItem = isBundle
    ? product.data
    : product.selectedOffer || product.data;

  const count = currentItem ? cartStore.getCartItemCountById(currentItem) : 0;

  const { limit } = useProductLimit({
    offer: currentItem,
  });

  const lowestMultibuyLevel = currentItem?.getLowestMultibuyLevel()?.quantity;

  const isMultiBuy = !isBundle && Boolean(lowestMultibuyLevel);

  const { vibrateOnChange } = useCartVibration({
    isMultibuy: isMultiBuy,
    requiredQuantity: lowestMultibuyLevel,
    currentCount: count,
  });

  const handleChangeCount = (count: number, action: 'add' | 'remove') => {
    vibrateOnChange(count, action);

    if (currentItem) {
      cartStore.setCartItemCountByProduct(
        {
          ...currentItem,
          ratingAverage: currentItem?.ratingAverage ?? 0,
          ratingMarksCount: currentItem?.ratingMarksCount ?? 0,
        },
        count,
        action,
        'product_main',
      );
    }
  };

  const handleDecrement = () => {
    if (count - 1 <= 0) {
      handleChangeCount(0, 'remove');
      return;
    }
    handleChangeCount(count - 1, 'remove');
  };

  const handleIncrement = () => {
    const newValue = count + 1;

    if (newValue >= limit) {
      return;
    }

    handleChangeCount(newValue, 'add');
  };

  let modalContent: ReactNode = null;

  if (!product.data) {
    modalContent = <SelectProductSkeleton />;
  } else {
    modalContent = (
      <div className={styles.form}>
        {product.data && (
          <ProductCardV3
            product={product.data}
            variant="horizontal"
            productState={product}
            hideButton
          />
        )}
        <div className={styles.formInfo}>
          <div className={styles.choices}>
            {Object.values(currentProductFilters)
              .sort((a, b) => a.sorting - b.sorting || a.order - b.order)
              .map((filter) => (
                <ProductPropertyFilter
                  filter={filter}
                  handleChangeOffer={handleChangeOffer}
                  key={filter.code}
                />
              ))}
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.control}>
            {count > 0 ? (
              <Counter
                value={count}
                limit={limit}
                onDecrement={handleDecrement}
                onIncrement={handleIncrement}
              />
            ) : (
              <Button
                datatype="add-to-cart"
                onClick={() => handleChangeCount(count + 1, 'add')}
                size={SIZE.XL}
                variant="primaryOutlined"
                disabled={limit === 0}
                className={styles.button}
              >
                {limit === 0 ? t('soldOut') : t('addToCart')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal
      isShow={true}
      className={styles.selectSKU}
      onBackdropDismiss={backHandler}
    >
      <div className="popover-container input-form">
        <div className="popover">
          <ModalHeader title={modalTitle} onClose={backHandler} />

          {modalContent}
        </div>
      </div>
    </Modal>
  );
});
